import {Component, OnDestroy, OnInit} from '@angular/core';
import {LoginData} from 'app/models/login-data';
import {Router, Params, ActivatedRoute} from '@angular/router';
import {AuthService} from 'app/services/auth.service';
import {environment} from 'environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  rememberMe: boolean;
  redirectURL: string;
  isAuthenticating: boolean = false;

  private storageListener: (event: StorageEvent) => void;
  private messageListener: (event: MessageEvent) => void;

  constructor(private router: Router, private auth: AuthService, private route: ActivatedRoute) {}

  ngOnDestroy() {
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  ngOnInit() {
    console.log('LoginComponent: ngOnInit. Origin:', window.location.origin, 'Path:', window.location.pathname);
    console.log('LoginComponent: window.opener exists:', !!window.opener);

    if (environment.production) {
      // 1. Listen for completion messages from the popup (as a backup to localStorage)
      this.messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data && event.data.type === 'auth-success') {
          console.log('LoginComponent: Received auth-success message from popup');
          this.router.navigate(['competition']);
        }
      };
      window.addEventListener('message', this.messageListener);

      // 2. Check for auth code (checking route params, raw query string, and sessionStorage fallback)
      const urlParams = new URLSearchParams(window.location.search);
      const queryAuthCode = urlParams.get('auth');
      const storedAuthCode = sessionStorage.getItem('pending_auth_code');

      if (storedAuthCode) {
        console.log('LoginComponent: Found stored auth code in sessionStorage');
        sessionStorage.removeItem('pending_auth_code');
      }

      this.route.queryParams.subscribe((params: Params) => {
        const authCode = params['auth'] || queryAuthCode || storedAuthCode;
        console.log('LoginComponent: Auth code detected:', authCode ? 'YES' : 'NO');

        if (authCode) {
          console.log('LoginComponent: Starting authentication process...');
          this.isAuthenticating = true;
          this.auth.authentication({ code: authCode }).subscribe({
            next: () => {
              console.log('LoginComponent: Authentication successful');
              if (window.opener) {
                console.log('LoginComponent: Inside popup, notifying opener and closing');
                window.opener.postMessage({ type: 'auth-success' }, window.location.origin);
                window.close();
              } else {
                this.router.navigate(['competition']);
              }
            },
            error: err => {
              console.error('LoginComponent: Authentication failed:', err);
              this.isAuthenticating = false;
            }
          });
        } else if (!window.opener) {
          if (this.auth.getTokenLocal()) {
             if (this.auth.isTokenExpired()) {
               console.log('LoginComponent: Token exists but is expired. Clearing token...');
               this.auth.logout();
             } else {
               console.log('LoginComponent: Token already exists and is valid, navigating...');
               this.router.navigate(['competition']);
               return;
             }
          }
          this.startLoginFlow();
        }
      });
    } else {
      // Local development fallback
      this.auth.authentication({ code: 'local-env-dev' }).subscribe(() => {
        this.router.navigate(['competition']);
      });
    }
  }

  private startLoginFlow() {
    console.log('LoginComponent: Starting login flow (opening popup)...');
    
    // 1. Listen for the token appearing in localStorage (set by the popup after it authenticates)
    this.storageListener = (event: StorageEvent) => {
      if (event.key === 'sessionIdToken' && event.newValue) {
        console.log('LoginComponent: Token detected in localStorage, navigating...');
        window.removeEventListener('storage', this.storageListener);
        this.router.navigate(['competition']);
      }
    };
    window.addEventListener('storage', this.storageListener);

    // 2. Open the login popup
    this.auth.redirect().subscribe({
      next: r => {
        if (r && r.location) {
          console.log('LoginComponent: Opening Azure AD login popup...');
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          
          window.open(
            r.location,
            'AzureADAuthPopup',
            `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=1,resizable=1,location=1,menuBar=0`
          );
        }
      },
      error: err => {
        console.error('LoginComponent: Failed to get redirect URL:', err);
      }
    });
  }

  login(data: LoginData) {
    this.startLoginFlow();
  }
}
